'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AskInstrumentationTransport } from '#pplx/frontend/apps/ask/analytics/AskInstrumentationTransport.js';
import { LoginSuccessSideEffects } from '#pplx/frontend/apps/ask/auth/LoginSuccessSideEffects.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { LoggerAgent } from '#pplx/frontend/logger/LoggerAgent.js';
import { MetricsAgent } from '#pplx/frontend/metrics/MetricsAgent.js';
import { isWindowsApp as parseIsWindowsApp } from '#pplx/frontend/user-agent-parser/browser.js';
import React, { useMemo } from 'react';
/**
 * Resolve the plan-level subscription tier attached to every analytics event as
 * `@context.tier`: 'free' | 'basic' | 'viewer' | 'pro' | 'max'. Mirrors the active-status
 * and tier rules in `useIsSubscribed()` so the facet taxonomy stays in sync:
 *  - active statuses are active / trialing / white_glove_past_due
 *  - an active sub with tier 'max' → 'max', 'basic' → 'basic'
 *  - an active sub with tier 'pro' OR an empty/missing tier → 'pro' (pro is the
 *    default paid tier and legacy subs may not carry an explicit tier; matches
 *    `useIsSubscribed()`'s `isPro = active && (tier === 'pro' || !tier)`)
 *  - anything not active resolves to 'free'
 *
 * Enterprise is deliberately NOT folded into this field. It is an orthogonal
 * dimension surfaced separately as `@context.isEnterprise` (see
 * `resolveIsEnterprise`). Keeping tier and enterprise separate lets dashboards
 * facet by either alone or cross them, instead of pre-baking 'Enterprise
 * Pro'/'Enterprise Max' labels.
 */
function resolveTier(user) {
    const status = user.subscription_status ?? 'none';
    const isActive = status === 'active' || status === 'trialing' || status === 'white_glove_past_due';
    if (!isActive) {
        return 'free';
    }
    if (user.subscription_tier === 'max') {
        return 'max';
    }
    if (user.subscription_tier === 'viewer') {
        return 'viewer';
    }
    if (user.subscription_tier === 'basic') {
        return 'basic';
    }
    // 'pro' or an empty/missing tier on an active sub — pro is the default paid
    // tier (mirrors `useIsSubscribed()`'s `isPro` fallback for legacy subs).
    return 'pro';
}
/**
 * Whether the user belongs to an enterprise org, attached to every analytics
 * event as the groupable `@context.isEnterprise` facet. Uses the canonical
 * `useIsEnterpriseUser` rule: an org UUID that is present and not the sentinel
 * 'none'. Orthogonal to `resolveTier` — an enterprise user still has a
 * free/pro/max plan tier.
 */
function resolveIsEnterprise(orgUuid) {
    return Boolean(orgUuid && orgUuid !== 'none');
}
export const Instrumentation = React.memo(function Instrumentation() {
    const { session, status } = useSession();
    const relevantSession = useMemo(() => {
        if (status !== 'authenticated') {
            return;
        }
        return {
            id: session.user.id,
            name: session.user.name,
            email: session.user.email,
            orgId: session.user.org_uuid,
            isPro: !!session.user.subscription_status && session.user.subscription_status !== 'none',
            tier: resolveTier(session.user),
            isEnterprise: resolveIsEnterprise(session.user.org_uuid),
        };
    }, [session, status]);
    const isWindowsApp = parseIsWindowsApp(globalThis.navigator?.userAgent ?? '');
    const isWindowsDesktopBuild = !!import.meta.env.VITE_IS_PERPLEXITY_DESKTOP_BUILD || isWindowsApp;
    return (_jsxs(_Fragment, { children: [_jsx(AskInstrumentationTransport, {}), _jsx(LoginSuccessSideEffects, {}), relevantSession && (_jsxs(_Fragment, { children: [_jsx(LoggerAgent, { session: relevantSession, isWindowsApp: isWindowsDesktopBuild }), _jsx(MetricsAgent, { session: relevantSession, isWindowsApp: isWindowsDesktopBuild })] }))] }));
});
//# sourceMappingURL=Instrumentation.js.map